import { Box, Button, Divider, Drawer, IconButton, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Close as CloseIcon, MenuBook as MenuBookIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HelpTopicView from './HelpTopicView';
import { helpTheme } from './helpTheme';

interface HelpDrawerProps {
    open: boolean;
    topicId: string;
    onClose: () => void;
}

/** Slide-in drawer showing the help topic for the current page. */
const HelpDrawer = ({ open, topicId, onClose }: HelpDrawerProps) => {
    const { t } = useTranslation('help');
    const navigate = useNavigate();

    const openFullDocumentation = () => {
        onClose();
        navigate(`/help#${topicId}`);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{ paper: { sx: { width: { xs: '100%', sm: 460 }, maxWidth: '100%' } } }}
        >
            <ThemeProvider theme={helpTheme}>
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                    bgcolor="background.paper"
                    color="text.primary"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={2}
                        py={1.5}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">
                            {t('drawer.onThisPage')}
                        </Typography>
                        <IconButton onClick={onClose} size="small" aria-label={t('drawer.close')}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Divider />

                    <Box sx={{ px: 2.5, py: 2, overflowY: 'auto', flex: 1 }}>
                        <HelpTopicView topicId={topicId} />
                    </Box>

                    <Divider />
                    <Box px={2.5} py={1.5}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<MenuBookIcon />}
                            onClick={openFullDocumentation}
                        >
                            {t('drawer.openFull')}
                        </Button>
                    </Box>
                </Box>
            </ThemeProvider>
        </Drawer>
    );
};

export default HelpDrawer;
